import { yupResolver } from '@hookform/resolvers/yup'
import { FragmentOf, graphql, readFragment } from 'gql.tada'
import Router from 'next/router'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { useMutation } from 'urql'
import { object, ref, string } from 'yup'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  WebPanel,
  WebPanelItem,
  WebPanelItemAction,
  WebPanelItemContent,
  WebPanelItemLabel,
} from '@/components/web-panel'
import { BasicInfo_ViewerFragment } from '@/gql/generated/graphql'
import { ProfileNameUpdateMutation, ProfilePasswordUpdateMutation } from '@/queries'
import { passwordRegex } from '@/utils/password_validation'
import { cn } from '@/utils/tw-helpers'

import { CancelSection } from './CancelSection'
import { NotificationSection } from './NotificationSection'

interface Props {
  viewer: null | undefined | FragmentOf<typeof BasicInfo_viewer>
}

export const AccountSettings = (props: Props): JSX.Element => {
  const basicData = readFragment(BasicInfo_viewer, props.viewer)

  if (!basicData) return <></>

  return (
    <section className="mx-auto max-w-[940px]">
      <h3 className="mb-2.5 text-base font-bold md:mb-3.5 md:text-[17px] lg:mb-5 lg:text-xl">
        基本情報
      </h3>
      <WebPanel>
        <WebPanelItem>
          <WebPanelItemLabel>ログインID</WebPanelItemLabel>
          <WebPanelItemContent className="overflow-hidden">
            <span className="block truncate text-[12px] font-bold text-[#919191] no-underline md:text-[14px] lg:text-[15px]">
              {basicData.email}
            </span>
          </WebPanelItemContent>
          <WebPanelItemAction>
            <Button
              variant="outline"
              size="sm"
              className="ml-2 h-[38px] w-[52px] rounded-lg text-xs md:h-[44px] md:w-[74px] md:rounded-xl md:text-[15px] lg:w-[80px]"
              onClick={() => {
                Router.push('/web/change_email')
              }}
            >
              変更
            </Button>
          </WebPanelItemAction>
        </WebPanelItem>

        <PasswordForm />
        <UserNameForm userData={basicData} />
      </WebPanel>

      <NotificationSection />

      <CancelSection canDelete={basicData.canDelete} />
    </section>
  )
}

type PasswordFormData = {
  currentPassword: string
  newPassword: string
  newPasswordConfirmation: string
}

const passwordSchema = object({
  currentPassword: string().required('現在のパスワードを入力してください'),
  newPassword: string()
    .notOneOf(
      [ref('currentPassword')],
      '新しいパスワードは現在のパスワードと異なるものを入力してください'
    )
    .required('新しいパスワードが条件を満たしていません')
    .matches(passwordRegex, '新しいパスワードが条件を満たしていません'),
  newPasswordConfirmation: string()
    .oneOf([ref('newPassword')], '新しいパスワードと確認用パスワードが異なります')
    .required('新しいパスワードと確認用パスワードが異なります'),
})

const PasswordForm = () => {
  const {
    register,
    handleSubmit,
    trigger,
    reset,
    formState: { errors, touchedFields, dirtyFields },
  } = useForm<PasswordFormData>({
    mode: 'onTouched',
    resolver: yupResolver(passwordSchema),
  })

  const [passwordUpdateResult, profilePasswordUpdateMutation] = useMutation(
    ProfilePasswordUpdateMutation
  )

  const [isEditingPassword, setEditingPassword] = useState<boolean>(false)

  const handleNewPasswordSetup = async ({ currentPassword, newPassword }: PasswordFormData) => {
    const valid = await trigger(['currentPassword', 'newPassword'])

    if (!valid) return

    try {
      const { error, data } = await profilePasswordUpdateMutation({
        input: {
          currentPassword: currentPassword ?? '',
          newPassword: newPassword ?? '',
        },
      })

      if (!error && !data?.profilePasswordUpdate?.errors?.length) {
        toast.success('パスワードを更新しました', {
          toastId: 'update-password-success',
          position: 'bottom-right',
        })
        setEditingPassword(false)
        return
      }

      toast.error('パスワード変更に失敗しました。', {
        toastId: 'update-password-error',
        position: 'bottom-right',
      })
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <form
      onSubmit={handleSubmit((formData) => handleNewPasswordSetup(formData))}
      className="border-b border-b-outline"
    >
      <WebPanelItem>
        <WebPanelItemLabel>パスワード</WebPanelItemLabel>
        {isEditingPassword ? (
          <WebPanelItemContent className="flex w-full flex-wrap items-center py-2.5 md:flex-nowrap md:py-6">
            <div className="w-full md:mr-3">
              <div className="flex flex-col">
                <Label htmlFor="currentPassword" className="mb-[7px] text-[14px]">
                  現在のパスワード
                </Label>
                <Input
                  id="currentPassword"
                  type="password"
                  placeholder="パスワード"
                  error={!!errors.currentPassword}
                  className={cn('h-[40px] w-full md:h-[44px] md:max-w-[386px] lg:max-w-[400px]')}
                  {...register('currentPassword', {
                    onChange: () => {
                      if (touchedFields.newPassword && dirtyFields.currentPassword) {
                        trigger('newPassword')
                      }
                    },
                  })}
                />
                {errors.currentPassword ? (
                  <p className="mt-1 text-[13px] text-caution">{errors.currentPassword.message}</p>
                ) : null}
              </div>
              <div className="mt-3 flex flex-col">
                <Label htmlFor="newPassword" className="mb-[7px] text-[14px]">
                  新しいパスワード
                </Label>
                <Input
                  id="newPassword"
                  type="password"
                  placeholder="パスワード"
                  error={!!errors.newPassword}
                  className={cn('h-[40px] w-full md:h-[44px] md:max-w-[386px] lg:max-w-[400px]')}
                  {...register('newPassword', {
                    onChange: () => {
                      if (touchedFields.newPasswordConfirmation && dirtyFields.newPassword) {
                        trigger('newPasswordConfirmation')
                      }
                    },
                  })}
                />
                <p className="mt-[1px] text-xs font-medium text-secondary-text">
                  ※半角英数字12文字以上
                </p>
                {errors.newPassword ? (
                  <p className="mt-1 text-[13px] text-caution">{errors.newPassword.message}</p>
                ) : null}
              </div>
              <div className="mt-3 flex flex-col">
                <Label htmlFor="newPasswordConfirmation" className="mb-[7px] text-[14px]">
                  確認用パスワード
                </Label>
                <Input
                  id="newPasswordConfirmation"
                  type="password"
                  placeholder="パスワード"
                  error={!!errors.newPasswordConfirmation}
                  className={cn('h-[40px] w-full md:h-[44px] md:max-w-[386px] lg:max-w-[400px]')}
                  {...register('newPasswordConfirmation')}
                />
                {errors.newPasswordConfirmation ? (
                  <p className="mt-1 text-[13px] text-caution">
                    {errors.newPasswordConfirmation.message}
                  </p>
                ) : null}
              </div>
            </div>
            <WebPanelItemAction
              role="group"
              className="mt-[15px] flex justify-end gap-2.5 md:mt-0 md:justify-normal"
            >
              <Button
                variant="secondary"
                size="sm"
                type="submit"
                shadow={true}
                className="h-[38px] w-[60px] rounded-lg text-xs md:h-[44px] md:w-[74px] md:rounded-xl md:text-[15px]"
                disabled={
                  !!errors.currentPassword ||
                  !!errors.newPassword ||
                  !!errors.newPasswordConfirmation
                }
              >
                変更
              </Button>

              <Button
                variant="light"
                className="h-[38px] w-[60px] rounded-lg text-xs md:h-[44px] md:w-[74px] md:rounded-xl md:text-[15px]"
                shadow={true}
                size="sm"
                type="button"
                onClick={() => {
                  let newData = { ...passwordUpdateResult.data }
                  if (
                    newData?.profilePasswordUpdate &&
                    (!newData.profilePasswordUpdate.errors ||
                      newData.profilePasswordUpdate.errors.length === 0)
                  ) {
                    newData.profilePasswordUpdate.errors = []
                  }

                  passwordUpdateResult.data = {
                    profilePasswordUpdate: newData?.profilePasswordUpdate ?? null,
                  }

                  setEditingPassword(false)
                }}
              >
                やめる
              </Button>
            </WebPanelItemAction>
          </WebPanelItemContent>
        ) : (
          <>
            <WebPanelItemContent>
              <span className="text-xs font-bold text-[#919191] no-underline md:text-[14px] lg:text-[15px]">
                ********
              </span>
            </WebPanelItemContent>
            <WebPanelItemAction>
              <Button
                variant="outline"
                size="sm"
                className="ml-2 h-[38px] w-[52px] rounded-lg text-xs md:h-[44px] md:w-[74px] md:rounded-xl md:text-[15px] lg:w-[80px]"
                onClick={() => {
                  reset()
                  setEditingPassword(true)
                }}
              >
                変更
              </Button>
            </WebPanelItemAction>
          </>
        )}
      </WebPanelItem>
    </form>
  )
}

type UserNameFormData = {
  firstName: string
  lastName: string
}

const userNameSchema = object({
  firstName: string().max(5, '名は5文字以下で入力してください').required('名を入力してください'),
  lastName: string().max(5, '姓は5文字以下で入力してください').required('姓を入力してください'),
})

const UserNameForm = ({ userData }: { userData: BasicInfo_ViewerFragment }) => {
  const {
    register,
    watch,
    setValue,
    handleSubmit,
    trigger,
    formState: { errors },
  } = useForm<UserNameFormData>({
    mode: 'onTouched',
    resolver: yupResolver(userNameSchema),
    defaultValues: {
      firstName: userData?.firstName,
      lastName: userData?.lastName,
    },
  })

  const firstName = watch('firstName')
  const lastName = watch('lastName')

  const [isEditingName, setEditingName] = useState<boolean>(false)
  const [currentFirstName, setCurrentFirstName] = useState<string>(userData?.firstName || '')
  const [currentLastName, setCurrentLastName] = useState<string>(userData?.lastName || '')

  const [_, profileNameUpdateMutation] = useMutation(ProfileNameUpdateMutation)

  const handleUserNameSetup = async (formData: UserNameFormData) => {
    const valid = await trigger(['firstName', 'lastName'])

    if (!valid) return

    const result = await profileNameUpdateMutation({
      input: {
        firstName: formData.firstName ?? '',
        lastName: formData.lastName ?? '',
      },
    })

    if (result.error || result.data?.profileNameUpdate?.errors) {
      return toast.error('契約者名の変更に失敗しました', {
        toastId: 'profile-name-update-failed',
        position: 'bottom-right',
      })
    }

    if (!result.data) return

    setCurrentFirstName(formData.firstName)
    setCurrentLastName(formData.lastName)

    setEditingName(false)

    toast.success('契約者名を変更しました', {
      toastId: 'profile-name-update-success',
      position: 'bottom-right',
    })
  }

  const handleCancelEdit = () => {
    setValue('firstName', currentFirstName)
    setValue('lastName', currentLastName)

    setEditingName(false)
  }

  return (
    <form onSubmit={handleSubmit(handleUserNameSetup)}>
      <WebPanelItem>
        <WebPanelItemLabel>契約者名</WebPanelItemLabel>
        {isEditingName ? (
          <WebPanelItemContent className="flex w-full flex-wrap items-center py-2.5 md:flex-nowrap md:py-6">
            <div className="flex max-w-[320px] flex-row gap-2.5">
              <div>
                <Input
                  type="text"
                  value={lastName}
                  error={!!errors.lastName}
                  className="h-[40px] w-full md:h-[44px] md:w-[188px] lg:w-[195px]"
                  placeholder="姓"
                  {...register('lastName')}
                />

                {!!errors.lastName && (
                  <p className="mt-1 text-[10px] leading-tight text-caution md:text-xs">
                    {errors.lastName.message}
                  </p>
                )}
              </div>
              <div>
                <Input
                  type="text"
                  value={firstName}
                  error={!!errors.firstName}
                  className="h-[40px] w-full md:h-[44px] md:w-[188px] lg:w-[195px]"
                  placeholder="名"
                  {...register('firstName')}
                />
                {!!errors.firstName && (
                  <p className="mt-1 text-[10px] leading-tight text-caution md:text-xs">
                    {errors.firstName.message}
                  </p>
                )}
              </div>
            </div>
            <WebPanelItemAction
              role="group"
              className="mt-[15px] flex justify-end gap-2.5 md:mt-0 md:justify-normal"
            >
              <Button
                variant="secondary"
                shadow={true}
                size="sm"
                className="h-[38px] w-[60px] rounded-lg text-xs md:h-[44px] md:w-[74px] md:rounded-xl md:text-[15px]"
                type="submit"
                disabled={!!errors.firstName || !!errors.lastName}
              >
                <span className="opacity-1 inline-flex items-center justify-center">変更</span>
              </Button>
              <Button
                variant="light"
                className="h-[38px] w-[60px] rounded-lg text-xs md:h-[44px] md:w-[74px] md:rounded-xl md:text-[15px]"
                shadow={true}
                size="sm"
                type="button"
                onClick={handleCancelEdit}
              >
                <span className="opacity-1 inline-flex items-center justify-center">やめる</span>
              </Button>
            </WebPanelItemAction>
          </WebPanelItemContent>
        ) : (
          <>
            <WebPanelItemContent>
              <span className="text-xs font-bold text-[#919191] no-underline md:text-[14px] lg:text-[15px]">
                {formatName(currentFirstName, currentLastName)}
              </span>
            </WebPanelItemContent>

            <WebPanelItemAction>
              <Button
                variant="outline"
                size="sm"
                className="ml-2 h-[38px] w-[52px] rounded-lg text-xs md:h-[44px] md:w-[74px] md:rounded-xl md:text-[15px] lg:w-[80px]"
                onClick={() => setEditingName(true)}
              >
                変更
              </Button>
            </WebPanelItemAction>
          </>
        )}
      </WebPanelItem>
    </form>
  )
}

function formatName(firstName?: string | null, lastName?: string | null) {
  return [lastName, firstName].filter(Boolean).join(' ')
}

export const BasicInfo_viewer = graphql(`
  fragment BasicInfo_viewer on User {
    email
    firstName
    lastName
    canDelete
  }
`)
